import { useState } from "react";
import type { OrderStatus, SellerOrderDetail } from "@/lib/api/types";
import { useUpdateOrderStatusMutation } from "./useUpdateOrderStatusMutation";
import { isEtaRelevantStatus } from "../components/order-status";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";

type UseQuickStatusActionParams = {
  storeSlug: string;
  orderId: string;
  onSuccess: (updatedOrder: SellerOrderDetail) => void;
};

export function useQuickStatusAction({
  storeSlug,
  orderId,
  onSuccess,
}: UseQuickStatusActionParams) {
  const { mutateAsync: updateSellerOrderStatus } = useUpdateOrderStatusMutation();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitAction = async (
    targetStatus: OrderStatus,
    etaDays?: number,
  ): Promise<void> => {
    // Client-side ETA policy lives in `orderStatusMachine.ts` (consumed by
    // the quick-action sheet). Don't duplicate it here — the sheet only
    // passes `etaDays` when its OrderTransition.needsEta is true. All this
    // hook needs to do is bounce an invalid number back; missing is fine.
    if (
      etaDays !== undefined &&
      etaDays !== null &&
      (!Number.isFinite(etaDays) || etaDays < 0)
    ) {
      setError("ETA_INVALID");
      return;
    }

    setError(null);
    setIsSaving(true);

    const payload: {
      status: OrderStatus;
      statusEtaDays: number | null;
      declineReasonCode?: "amount_not_received";
    } = {
      status: targetStatus,
      statusEtaDays: isEtaRelevantStatus(targetStatus)
        ? (etaDays ?? null)
        : null,
    };

    if (targetStatus === "declined") {
      payload.declineReasonCode = "amount_not_received";
    }

    try {
      const updatedOrder = await updateSellerOrderStatus({
        storeSlug,
        orderId,
        ...payload,
      });
      onSuccess(updatedOrder);
    } catch (caughtError) {
      if (caughtError instanceof ApiJsonClientError) {
        const normalized = caughtError.message.toLowerCase();
        if (
          normalized.includes("statusetadays") ||
          normalized.includes("eta") ||
          normalized.includes("status eta")
        ) {
          setError("ETA_VALIDATION");
        } else if (normalized.includes("stale") || normalized.includes("conflict")) {
          setError("STALE_STATUS");
        } else {
          setError("GENERIC");
        }
      } else {
        setError("GENERIC");
      }
    } finally {
      setIsSaving(false);
    }
  };

  return {
    submitAction,
    isSaving,
    error,
    clearError: () => setError(null),
  };
}
